import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Plus, CalendarDays, List, CalendarPlus } from "lucide-react";
import { MONTHS, DAYS, DAYS_LONG, STATUSES, FORMATS, toIso, addDays, weekStart, fmtDay, inMonth, weeksOfMonth, inWeek, dow } from "./contentUtils";
import { DarkPanel, Pill, IconBubble, AccentButton, CheckDot, StatusBadge, HATCH } from "./StatusBadge";

function Card({ item, onOpen }) {
  return (
    <button data-testid={`cal-item-${item.id}`} draggable onDragStart={(e) => { e.dataTransfer.setData("text/plain", item.id); e.dataTransfer.effectAllowed = "move"; }} onClick={() => onOpen(item.id)}
      className="block w-full cursor-grab rounded-xl bg-white p-2.5 text-left shadow-[0_2px_10px_rgba(45,24,30,0.06)] transition-transform hover:-translate-y-0.5 active:cursor-grabbing">
      <span className="line-clamp-2 text-xs font-medium leading-snug text-ink">{item.title || "Sans titre"}</span>
      <span className="mt-2 flex items-center justify-between gap-1">
        <span className="truncate rounded-full bg-bordeaux-tint px-2 py-0.5 text-[10px] font-medium text-bordeaux">• {item.format || "Libre"}</span>
        <CheckDot on={item.status === "publie"} />
      </span>
    </button>
  );
}

function DayCell({ date, faded, items, onDrop, onAdd, onOpen, tall }) {
  const [over, setOver] = useState(false);
  const iso = toIso(date);
  const today = iso === toIso(new Date());
  return (
    <div data-testid={`cal-day-${iso}`} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={(e) => { e.preventDefault(); setOver(false); const id = e.dataTransfer.getData("text/plain"); if (id) onDrop(id, iso); }}
      className={`group rounded-2xl p-2 transition-colors ${tall ? "min-h-[300px]" : "min-h-[124px]"} ${over ? "bg-bordeaux-tint ring-2 ring-bordeaux-border" : "bg-[#F5F0EA]"} ${today ? "ring-2 ring-bordeaux" : ""} ${faded ? "opacity-40" : ""}`}>
      <div className="mb-2 flex items-center justify-between">
        <span className={`inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-xs font-medium ${today ? "bg-bordeaux text-white" : "text-ink-muted"}`}>{tall ? `${DAYS[(date.getDay() + 6) % 7]} ${date.getDate()}` : date.getDate()}</span>
        <button data-testid={`cal-add-${iso}`} onClick={() => onAdd(iso)} className="rounded-full p-0.5 text-ink-muted transition-opacity hover:text-bordeaux lg:opacity-0 lg:group-hover:opacity-100"><Plus size={14} /></button>
      </div>
      <div className="space-y-1.5">{items.map((i) => <Card key={i.id} item={i} onOpen={onOpen} />)}</div>
    </div>
  );
}

function Hero({ items, y, m, onOpen, goIdeas }) {
  const month = items.filter((i) => inMonth(i.date, y, m));
  const pub = month.filter((i) => i.status === "publie").length;
  const today = toIso(new Date());
  const next = items.filter((i) => i.date && i.date >= today && i.status !== "publie").sort((a, b) => a.date.localeCompare(b.date))[0];
  const weeks = weeksOfMonth(y, m).map((ws) => { const w = month.filter((i) => inWeek(i.date, ws)); return { p: w.filter((i) => i.status === "publie").length, t: w.length }; });
  const max = Math.max(1, ...weeks.map((w) => w.t));
  return (
    <DarkPanel data-testid="cal-hero">
      <div className="grid min-w-0 gap-6 lg:grid-cols-[1fr_1.15fr]">
        <div className="min-w-0 space-y-4">
          <Pill dark>Ce mois-ci</Pill>
          <IconBubble icon={CalendarDays} dark />
          <div><h3 data-testid="cal-hero-title" className="break-words font-sans text-2xl font-medium sm:text-3xl">{month.length} posts prévus · {pub} publiés</h3><p className="mt-1 text-sm text-white/60">{next ? `Prochain : ${next.title || "Sans titre"} · ${fmtDay(next.date)}` : "Rien de prévu pour la suite"}</p></div>
          <AccentButton testid="cal-hero-plan" icon={CalendarPlus} onClick={goIdeas}>Planifier une idée</AccentButton>
          <div className="flex gap-4 text-[11px] text-white/50"><span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-bordeaux" /> Publié</span><span className="flex items-center gap-1.5"><span className="h-2 w-3 rounded-sm" style={HATCH} /> Prévu</span></div>
        </div>
        <div className="grid min-w-0 grid-cols-1 gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:grid-cols-[1fr_170px]">
          <div className="flex items-end justify-around gap-2">
            {weeks.map((w, n) => (
              <div key={n} className="flex flex-col items-center gap-2">
                <div className="relative flex h-36 w-7 flex-col sm:w-9 justify-end overflow-hidden rounded-full bg-white/5">
                  <div className="w-full rounded-t-full" style={{ height: `${((w.t - w.p) / max) * 100}%`, ...HATCH }} />
                  <div className="w-full bg-bordeaux" style={{ height: `${(w.p / max) * 100}%`, boxShadow: "0 0 24px rgba(123,17,58,0.8)" }} />
                </div>
                <span className="text-[10px] text-white/50">S{n + 1}</span>
              </div>
            ))}
          </div>
          <button data-testid="cal-next-post" onClick={() => next && onOpen(next.id)} className="flex flex-col justify-between rounded-2xl border border-bordeaux/60 bg-bordeaux/10 p-3 text-left">
            <span className="text-[11px] text-white/50">Prochain post</span>
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-bordeaux"><Plus size={18} /></span>
            <span className="line-clamp-2 text-xs text-white/80">{next ? next.title || "Sans titre" : "Aucun post prévu"}</span>
          </button>
        </div>
      </div>
    </DarkPanel>
  );
}

function ListView({ items, onOpen }) {
  const list = [...items].sort((a, b) => a.date.localeCompare(b.date));
  if (!list.length) return <p className="py-10 text-center text-sm italic text-ink-muted">Aucun contenu ce mois-ci.</p>;
  return (
    <div className="divide-y divide-line/60">
      {list.map((i) => (
        <button key={i.id} data-testid={`cal-list-${i.id}`} onClick={() => onOpen(i.id)} className="flex w-full items-center gap-4 rounded-2xl px-3 py-3 text-left transition-colors hover:bg-cream">
          <span className="flex w-14 shrink-0 flex-col items-center rounded-xl bg-[#2D181E] py-1.5 text-white"><span className="text-[9px] uppercase text-white/60">{DAYS_LONG[dow(i.date)].slice(0, 3)}</span><span className="text-lg font-medium leading-none">{parseInt(i.date.slice(8), 10)}</span></span>
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">{i.title || "Sans titre"}</span>
          <span className="hidden rounded-full bg-bordeaux-tint px-2 py-0.5 text-[10px] text-bordeaux sm:inline">{i.format || "Libre"}</span>
          <StatusBadge status={i.status} />
        </button>
      ))}
    </div>
  );
}

export default function ContentCalendar({ items, actions, onOpen, onTab }) {
  const [view, setView] = useState("month");
  const [mode, setMode] = useState("cal");
  const [cursor, setCursor] = useState(new Date());
  const [fStatus, setFStatus] = useState("");
  const [fFormat, setFFormat] = useState("");
  const y = cursor.getFullYear();
  const m = cursor.getMonth();
  const visible = items.filter((i) => i.date && (!fStatus || i.status === fStatus) && (!fFormat || i.format === fFormat));
  const byDate = visible.reduce((a, i) => { (a[i.date] = a[i.date] || []).push(i); return a; }, {});
  const start = view === "month" ? weekStart(new Date(y, m, 1)) : weekStart(cursor);
  const count = view === "month" ? weeksOfMonth(y, m).length * 7 : 7;
  const days = Array.from({ length: count }, (_, i) => addDays(start, i));
  const shift = (n) => setCursor(view === "month" ? new Date(y, m + n, 1) : addDays(cursor, 7 * n));
  const title = view === "month" ? `${MONTHS[m]} ${y}` : `${fmtDay(toIso(start))} – ${fmtDay(toIso(addDays(start, 6)))}`;
  const drop = (id, iso) => { const it = items.find((x) => x.id === id); if (it && it.date !== iso) actions.updateItem(id, { date: iso, ...(it.status === "idee" ? { status: "planifie" } : {}) }); };
  const add = async (iso) => { const it = await actions.createItem({ date: iso, status: "planifie" }); if (it) onOpen(it.id); };
  const sel = "rounded-full border border-line bg-white px-4 py-2 text-xs text-ink outline-none focus:border-bordeaux";
  const circle = "flex h-9 w-9 items-center justify-center rounded-full border border-ink/80 text-ink transition-colors hover:bg-ink hover:text-white";

  return (
    <div className="space-y-5">
      <Hero items={items} y={y} m={m} onOpen={onOpen} goIdeas={() => onTab("idees", "mine")} />
      <div className="rounded-[28px] border border-line bg-white p-4 shadow-card sm:p-6">
        <div className="mb-5 inline-flex rounded-full bg-cream p-1">
          {[["cal", "Calendrier", CalendarDays], ["list", "Liste", List]].map(([k, l, I]) => <button key={k} data-testid={`cal-mode-${k}`} onClick={() => setMode(k)} className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs transition-colors ${mode === k ? "bg-white font-medium text-ink shadow-sm" : "text-ink-muted"}`}><I size={13} /> {l}</button>)}
        </div>
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button data-testid="cal-prev" onClick={() => shift(-1)} className={circle}><ChevronLeft size={16} /></button>
            <button data-testid="cal-today" onClick={() => setCursor(new Date())} className="rounded-full border border-ink/80 px-4 py-2 text-xs font-medium text-ink hover:bg-ink hover:text-white">Aujourd'hui</button>
            <button data-testid="cal-next" onClick={() => shift(1)} className={circle}><ChevronRight size={16} /></button>
            <p data-testid="cal-title" className="ml-2 font-sans text-lg font-medium capitalize text-ink">{title}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-full border border-line p-1">
              {[["month", "Mois"], ["week", "Semaine"]].map(([k, l]) => <button key={k} data-testid={`cal-view-${k}`} onClick={() => setView(k)} className={`rounded-full px-4 py-1 text-xs transition-colors ${view === k ? "bg-[#2D181E] text-white" : "text-ink-muted hover:text-ink"}`}>{l}</button>)}
            </div>
            <select data-testid="cal-filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={sel}><option value="">Tous les statuts</option>{STATUSES.filter((s) => s.key !== "idee").map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}</select>
            <select data-testid="cal-filter-format" value={fFormat} onChange={(e) => setFFormat(e.target.value)} className={sel}><option value="">Tous les formats</option>{FORMATS.map((f) => <option key={f}>{f}</option>)}</select>
          </div>
        </div>
        {mode === "list" ? <ListView items={visible.filter((i) => view === "month" ? inMonth(i.date, y, m) : inWeek(i.date, start))} onOpen={onOpen} /> : (
          <div className="overflow-x-auto">
            <div className="grid min-w-[760px] grid-cols-7 gap-2">
              {DAYS.map((d) => <div key={d} className="pb-1 text-center text-xs font-medium text-ink-muted">{d}</div>)}
              {days.map((d) => <DayCell key={toIso(d)} date={d} tall={view === "week"} faded={view === "month" && d.getMonth() !== m} items={byDate[toIso(d)] || []} onDrop={drop} onAdd={add} onOpen={onOpen} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
